import { useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

/** Where the billing page's back link goes, when not home. */
export interface BillingBack {
  label: string;
  to: string;
}

const KEY = 'floto.billing.back';

function save(back: BillingBack | null) {
  try {
    if (back) sessionStorage.setItem(KEY, JSON.stringify(back));
    else sessionStorage.removeItem(KEY);
  } catch {
    // Storage blocked: the back link falls back to home.
  }
}

function load(): BillingBack | undefined {
  try {
    const raw = sessionStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as BillingBack) : undefined;
  } catch {
    return undefined;
  }
}

/**
 * The back link for the billing page: to the room that sent the user to top
 * up, rather than home and a click back in.
 *
 * Arrives as router state (`{ back }`), which Stripe's checkout drops on the
 * way out and back — so it is kept for the tab, and read again on the
 * checkout's return or a step back from Usage. Any other way in forgets it.
 */
export function useBillingBack(): BillingBack | undefined {
  const location = useLocation();
  const [params] = useSearchParams();
  // Read once, on arrival: the page strips the checkout params straight away.
  const [back] = useState(() => {
    const state = location.state as {
      back?: BillingBack;
      from?: string;
    } | null;
    if (state?.back) {
      save(state.back);
      return state.back;
    }
    if (
      params.has('session_id') ||
      params.has('topup') ||
      state?.from === 'usage'
    ) {
      return load();
    }
    save(null);
    return undefined;
  });
  return back;
}
